import { useCallback, useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import InlineActions from "components/Actions/dialogs/components/DialogActions";
import { DialogProps, USER_ACTIONS } from "components/Actions/types";
import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import {
    ENDPOINTS,
    FILTER_NAMES,
    isInternalTypeCompany,
    maxDialogWidth,
    Query,
    TABLE_NAMES,
    TRUE,
    useApi,
    useEmailValidation,
    useIsMounted,
    useMediaQueries,
    useUserAccessLevels
} from "shared";

import { DropdownValue } from "../../types";
import { AccessLevel, ExpirationDate, Role } from "../components";

const InviteUser = ({ isOpen, close }: DialogProps) => {
    const { rootCompanyType, setReloadItems } = useContext(ParamsContext);
    const { isSupportUser } = useContext(AuthContext);

    const isRootInternalTypeCompany = isInternalTypeCompany(rootCompanyType);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();
    const isMounted = useIsMounted();
    const { toMd, fromMd } = useMediaQueries();
    const { isAllowedToAssignAccessLevel } = useUserAccessLevels();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [roleResource, setRoleResource] = useState("");
    const [email, setEmail] = useState<string>("");
    const [company, setCompany] = useState<Query | null>(null);
    const [accessLevel, setAccessLevel] = useState<DropdownValue | null>(null);
    const [roles, setRoles] = useState<Query[]>([]);
    const [expiresAt, setExpiresAt] = useState<string | null>(null);
    const [hasExpiresAtError, setHasExpiresAtError] = useState(false);

    const { isEmailValid, emailError, clearEmailError, validateEmail } =
        useEmailValidation(email);

    const handleCloseDialog = useCallback(() => {
        clearEmailError();
        close(USER_ACTIONS.InviteUser);
    }, [close, clearEmailError]);

    const handleEmailChange = useCallback(
        (e: { target: { value: string } }) => {
            setEmail(e.target.value.trim());
            validateEmail(e);
            clearEmailError();
        },
        [clearEmailError, validateEmail]
    );

    const handleCompanyChange = (newCompany: Query | null) => {
        setCompany(newCompany);

        if (!isRootInternalTypeCompany) {
            setRoles([]);

            setRoleResource(
                newCompany
                    ? `${ENDPOINTS.Roles}?${FILTER_NAMES.IncludeStatic}=${TRUE}&${FILTER_NAMES.CompanyId}=${newCompany.id}`
                    : ""
            );
        }
    };

    const handleUserAccessLevelChange = (dropdownValue: DropdownValue) =>
        setAccessLevel(dropdownValue);

    const handleRoleChange = (newRoles: Query[]) => setRoles(newRoles);

    const handleRoleOptionsLoaded = (options: Query[]) => {
        // Auto select Administrator role initially for internal users
        if (isSupportUser) {
            const ADMINISTRATOR_ID = 1;

            const defaultOption = options.find(
                option => option.id === ADMINISTRATOR_ID
            );

            if (defaultOption) setRoles([defaultOption]);
        }
    };

    const handleExpirationDateChange = (newExpDate: string | null) =>
        setExpiresAt(newExpDate);

    const handleExpirationDateError = (error: string | null) =>
        setHasExpiresAtError(error !== null);

    const handleSubmit = useCallback(async () => {
        try {
            setIsLoading(true);

            const payload = {
                email,
                company_id: company?.id,
                expires_at: expiresAt,
                ...(accessLevel ? { access_level: accessLevel.id } : {}),
                ...(!isRootInternalTypeCompany
                    ? { role_ids: roles.map(role => role.id) }
                    : {})
            };

            const response = await postData(ENDPOINTS.Users, payload);

            if (isMounted) {
                handleCloseDialog();
                setReloadItems(TABLE_NAMES.Users);
                handleResponse(response);
            }
        } catch (error) {
            if (isMounted) {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    }, [
        email,
        company,
        isMounted,
        accessLevel,
        roles,
        expiresAt,
        isRootInternalTypeCompany,
        handleCloseDialog,
        handleResponse,
        postData,
        setReloadItems
    ]);

    const isReady =
        !hasExpiresAtError &&
        company !== null &&
        isEmailValid &&
        (isRootInternalTypeCompany || roles.length > 0);

    return (
        <Dialog
            title={t("Dialog##invite user")}
            description={t("Dialog##invite user description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={handleCloseDialog}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setRoleResource("");
                        setEmail("");
                        setCompany(null);
                        setAccessLevel(null);
                        setRoles([]);
                        setExpiresAt(null);
                        setHasExpiresAtError(false);
                    }
                }
            }}
            actions={
                <InlineActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    isReadyToConfirm={isReady}
                    confirmationButtonText={t("Button##invite")}
                    cancelButtonTestId="invite-user-cancel"
                    confirmationButtonTestId="invite-user-confirm"
                />
            }
            extendToMaxWidth={toMd}
            css={css(fromMd && maxDialogWidth)}
        >
            <InputField
                data-testid="invite-user-email"
                size={INPUT_FIELD_SIZES.Medium}
                id="standard-error-helper-text"
                fullWidth
                required
                readOnly={isLoading}
                onBlur={validateEmail}
                errorText={isEmailValid ? "" : emailError}
                onChange={handleEmailChange}
                labelLeft={t("Dialog##email")}
                placeholder={`${t("Dialog##example")} email@fota.com`}
                customStyle={{ marginTop: "8px" }}
            />

            <CompanyDropdown
                data-testid="invite-user-company"
                isRequired
                readOnly={isLoading}
                change={(_, val) => handleCompanyChange(val)}
                customStyle={{ margin: "16px 0px" }}
                isRootRequired
            />

            {isAllowedToAssignAccessLevel() && (
                <AccessLevel
                    isLoading={isLoading}
                    onChange={handleUserAccessLevelChange}
                />
            )}

            <Role
                isInvite
                isLoading={isLoading}
                roleResource={roleResource}
                roles={roles}
                companyValue={company}
                onChange={handleRoleChange}
                onInitialOptionsLoad={handleRoleOptionsLoaded}
            />

            <ExpirationDate
                expirationDate={expiresAt}
                isActive
                isLoading={isLoading}
                onExpirationDateChange={handleExpirationDateChange}
                onExpirationDateError={handleExpirationDateError}
                customStyle={{ marginBottom: "16px" }}
            />
        </Dialog>
    );
};

export default InviteUser;
